import { api } from '@goaltree/backend/convex/_generated/api'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useMutation } from 'convex/react'
import { useEffect, useRef } from 'react'

import { trackAuthMethod } from '@/lib/auth-analytics'
import { authClient } from '@/lib/auth-client'
import { setLastLogin } from '@/lib/last-login'
import { detectLocale } from '@/lib/locale'
import { optimisticSetLocale } from '@/lib/prefs-optimistic'
import { peekReturnTo, safeReturnTo, takeReturnTo } from '@/lib/return-to'
import { setSessionIndicator } from '@/lib/session-indicator'
import { pendingPlanReturnTo } from '@/store/onboarding.store'

export const Route = createFileRoute('/auth/google-onetap')({
  validateSearch: (search): { credential?: string; returnTo?: string } => ({
    credential: typeof search.credential === 'string' ? search.credential : undefined,
    returnTo: safeReturnTo(search.returnTo),
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const search = Route.useSearch()
  const { credential } = search
  const returnTo = search.returnTo ?? peekReturnTo() ?? pendingPlanReturnTo()
  const { isAuthenticated } = useConvexAuth()
  const navigate = useNavigate()
  const setLocale = useMutation(api.preferences.setLocale).withOptimisticUpdate(optimisticSetLocale)
  const attempted = useRef(false)

  useEffect(() => {
    if (!credential) {
      navigate({ to: '/auth/login', replace: true })
      return
    }
    if (attempted.current) return
    attempted.current = true

    setLastLogin('google')
    trackAuthMethod('google_onetap', 'login')
    void authClient.signIn.social({
      provider: 'google',
      idToken: { token: credential },
      callbackURL: returnTo ?? '/goals',
      newUserCallbackURL: returnTo ?? '/goals/new',
    })
  }, [credential, returnTo, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      setSessionIndicator()
      void setLocale({ locale: detectLocale() })
      navigate({ to: takeReturnTo(returnTo) ?? '/goals', replace: true })
    }
  }, [isAuthenticated, navigate, setLocale, returnTo])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isAuthenticated) navigate({ to: '/auth/login', replace: true })
    }, 10_000)
    return () => clearTimeout(timer)
  }, [navigate, isAuthenticated])

  return (
    <div className="flex h-svh items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
    </div>
  )
}
